import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import authProvider from "../authProvider";
import { GetCurrentUser } from "../api/users/getCurrentUser";
import PermissionDenied from "./permissionDenied";
import { permissions } from "../permissions";
import { useUserContext } from "../contexts/UserContext";
import { USER_DATA } from "../config/variables";

const Protected = ({ allowedRoles, requiredPermissions, requiredDynamicPermission, module }) => {
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);

  const { userData, setUserData, hasPermission, hasDynamicPermissions, canAccessModule, permissionsLoaded, iipAvailable } = useUserContext();
  const location = useLocation();
  const mfaData = JSON.parse(sessionStorage.getItem("mfa_user_sub") || null);

  useEffect(() => {
    setLoading(true);
    authProvider
      .checkAuth()
      .then(() => {
        authProvider
          .getCurrentUser()
          .then((user_data) => {
            setUserData(user_data);
            setLoading(false);
            setLoggedIn(true);
          })
          .catch(() => {
            GetCurrentUser()
              .then(({ data }) => {
                setLoading(false);
                setLoggedIn(true);
                setUserData(data);
                localStorage.setItem(USER_DATA, JSON.stringify(data));
              })
              .catch((error) => {
                setLoading(false);
                setLoggedIn(true);
                setUserData(null);
                console.error("Error getting user data:", error);
              });
          });
      })
      .catch(() => {
        setUserData(null);
        setLoading(false);
        setLoggedIn(false);
      });

    // eslint-disable-next-line
  }, []);

  const hasRequiredPermissions = () => {
    if (!requiredPermissions || !userData) return true;

    const userPermissions = permissions[userData?.role] || {};
    return Object.keys(requiredPermissions).every((key) => {
      return userPermissions[key]?.[requiredPermissions[key]] === true;
    });
  };

  // Gate purely on the fetched permission map, independent of static roles.
  const hasRequiredDynamicPermission = () => {
    const { resource, access = "read" } = requiredDynamicPermission;
    return hasPermission(resource, access);
  };

  if (mfaData && mfaData.mfa_required) {
    window.location.href = `/auth/mfa-${mfaData.mfa_registered ? "verify" : "setup"}`;
    return null;
  }

  if (loading) return null; // or spinner

  if (!loggedIn) return <Navigate to="/user/login" />;

  // Wait for the permission map before deciding, so we don't flash "Access
  // Denied" (or fire the page's API calls) while it loads.
  if (requiredDynamicPermission && !permissionsLoaded) return null;

  // Same for module routes. Guarded on userData?.id so an unresolved profile
  // doesn't block forever; the /card-issuing landing is always allowed below.
  if (
    module &&
    userData?.id &&
    !permissionsLoaded &&
    location.pathname !== "/card-issuing"
  ) {
    return null;
  }

  const isAllowed = requiredDynamicPermission
    ? hasRequiredDynamicPermission() ||
      // Legacy users (no dynamic map) fall back to a role check while the IIP
      // surface is up; dynamic users are gated purely on the permission.
      (iipAvailable &&
        !hasDynamicPermissions() &&
        allowedRoles &&
        allowedRoles.includes(userData?.role))
    : location.pathname === "/card-issuing" ||
      ((!allowedRoles || allowedRoles.includes(userData?.role)) &&
        hasRequiredPermissions() &&
        (canAccessModule ? canAccessModule(module) : true));

  return isAllowed ? (
    <Outlet />
  ) : (
    <PermissionDenied
      allowedRoles={allowedRoles}
      requiredPermissions={requiredPermissions}
    />
  );
};

export default Protected;